import { useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Collapse, Paper, Typography } from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { usePeriodosProcesos, useProcesosMios, useProcesosStaff } from "../../queries/useInstancias";
import { useMisPostulaciones } from "../../queries/usePostulaciones";
import { useAuthStore } from "../../stores/authStore";
import ProcesosStaff from "../../components/app/ProcesosStaff";
import ProcesosEstudiante from "../../components/app/ProcesosEstudiante";
import TareasExamen from "../../components/app/TareasExamen";
import { opcionesSemestre } from "../../utils/agruparStaff";
import { useFiltrosUrl } from "../../utils/estadoLista";

/**
 * "Procesos": las InstanciaPaso visibles para el usuario. La vista diverge por rol:
 *
 * - Los ajenos (uno guía, coguía o integra la comisión) van agrupados por semestre,
 *   con el resumen de carga (ver ProcesosStaff).
 * - Los propios (uno es el estudiante) van en un stepper con la acción actual arriba y
 *   el historial del ciclo abajo (ver ProcesosEstudiante).
 *
 * No son excluyentes: hay gente que se tituló acá y hoy guía o evalúa. Para esas
 * personas se muestran las dos, sin encabezados: las pestañas ya dicen qué es lo de
 * arriba y el botón plegado dice qué es lo de abajo. El corte se hace por dato (¿la
 * instancia es mía como estudiante?), no por rol, que es lo que antes escondía la mitad.
 *
 * Los ajenos van **primero**, y la titulación propia va plegada: para quien tiene los
 * dos, lo suyo ya pasó y lo que mira todas las semanas es la gente que guía o evalúa.
 * Sigue a un clic, pero no ocupa la pantalla cada vez que entra.
 *
 * El backend filtra por permisos implícitos (estudiante propio, guía/coguía, comisión).
 *
 * **Se pide en trozos.** Antes esto era una sola consulta con todo lo visible: para un
 * coordinador, miles de procesos con su comisión y sus evaluaciones, y la pantalla en
 * blanco hasta que llegara el último. Ahora son tres consultas independientes —lo
 * propio, lo ajeno del semestre que se mira, y los semestres que existen— y cada
 * parte se pinta apenas llega. El semestre elegido vive acá porque es lo que decide
 * qué se le pide al servidor, no solo qué se filtra en pantalla; y va en la URL para
 * que volver desde la ficha de alguien devuelva al semestre que se estaba mirando.
 */
export default function InstanciaPasos() {
  const { user } = useAuthStore();
  const puedeGuiar = !!user?.puede_ver_estudiantes;
  const esCoordinador = !!user?.es_coordinador_global;

  // En la URL, igual que la pestaña: entrar a un estudiante de un semestre viejo y
  // volver tiene que dejarte en ese semestre, no de vuelta en "lo que está en curso".
  const { leer, filtrar } = useFiltrosUrl();
  const semestre = leer("semestre");
  const setSemestre = (v: string) => filtrar({ semestre: v });
  const { data: mios, isLoading: cargandoMios, isError: errorMios } = useProcesosMios();
  const { data: ajenos, isLoading: cargandoAjenos, isFetching: buscandoAjenos, isError: errorAjenos } = useProcesosStaff(semestre);
  const { data: periodos } = usePeriodosProcesos();
  // El profe puede tener solicitudes por aceptar aunque todavía no guíe a nadie con proceso.
  const { data: postulaciones } = useMisPostulaciones(puedeGuiar);

  const [verMio, setVerMio] = useState(false);

  const semestres = useMemo(() => opcionesSemestre(periodos ?? []), [periodos]);
  // El panel de lo ajeno existe si la persona guía o evalúa algo **en algún semestre**,
  // no solo si tiene algo abierto hoy. `periodos` ya viene sin los procesos propios, así
  // que decirlo es preguntarle si trae algo.
  //
  // Con la condición anterior —solo lo abierto— a quien tenía todos sus estudiantes ya
  // cerrados se le escondía el panel completo, y el selector de semestre vive adentro:
  // no solo no los veía, no tenía desde dónde ir a buscarlos.
  const hayAjenos = (ajenos?.length ?? 0) > 0 || (postulaciones?.length ?? 0) > 0 || esCoordinador || (periodos?.length ?? 0) > 0;
  const ambos = (mios?.length ?? 0) > 0 && hayAjenos;
  // Cada mitad avisa por su cuenta: ninguna espera a la otra para pintarse.
  const isLoading = cargandoMios && cargandoAjenos;
  const isError = errorMios && errorAjenos;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Mis Procesos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Instancias del proceso de titulación en las que participas.
      </Typography>

      <TareasExamen />

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && <Alert severity="error">No se pudieron cargar los procesos.</Alert>}

      {!isLoading && !mios?.length && !hayAjenos && <VacioProcesos />}

      {ajenos && hayAjenos && (
        <Box sx={{ mb: ambos ? 5 : 0 }}>
          <ProcesosStaff
            instancias={ajenos}
            postulaciones={postulaciones ?? []}
            semestres={semestres}
            semestre={semestre}
            onSemestre={setSemestre}
            cargando={buscandoAjenos}
          />
        </Box>
      )}

      {mios &&
        mios.length > 0 &&
        (ambos ? (
          <Box>
            <Button
              size="small"
              variant="text"
              onClick={() => setVerMio((v) => !v)}
              endIcon={<ExpandMoreIcon sx={{ transform: verMio ? "rotate(180deg)" : "none", transition: "transform .2s" }} />}
            >
              {verMio ? "Ocultar" : "Ver"} mi proceso de titulación
            </Button>
            <Collapse in={verMio}>
              <Box sx={{ mt: 2 }}>
                <ProcesosEstudiante instancias={mios} />
              </Box>
            </Collapse>
          </Box>
        ) : (
          <ProcesosEstudiante instancias={mios} />
        ))}
    </Box>
  );
}

function VacioProcesos() {
  return (
    <Paper elevation={0} sx={{ p: 4, textAlign: "center", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Typography color="text.secondary">No tienes procesos asignados todavía.</Typography>
    </Paper>
  );
}
